import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { CheckCircle, Upload, XCircle } from 'lucide-react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { MURAL_LABEL } from '@/components/admin/CompanyHubInfo';

const SOURCE = { upload: 'carregado pela empresa', suggested: 'SUGERIDO a partir do site', admin: 'carregado por si' };

const AdminLogo = ({ url }) => {
  const [src, setSrc] = useState('');
  useEffect(() => {
    if (!url) return undefined;
    let obj = '';
    api.get(url.replace(/^\/api/, ''), { responseType: 'blob' }).then((r) => { obj = URL.createObjectURL(r.data); setSrc(obj); }).catch(() => setSrc(''));
    return () => { if (obj) URL.revokeObjectURL(obj); };
  }, [url]);
  return <div className="w-24 h-14 shrink-0 rounded border border-slate-200 bg-white flex items-center justify-center overflow-hidden">{src ? <img src={src} alt="" className="max-h-12 max-w-full object-contain" /> : <span className="text-[10px] text-slate-400">sem logo</span>}</div>;
};

const Entry = ({ e, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const file = useRef(null);
  const act = async (action) => {
    if (action === 'reject' && !window.confirm(`${e.status === 'approved' ? 'Retirar' : 'Rejeitar'} o logo de ${e.company_name}?`)) return;
    setBusy(true);
    try { await api.post(`/admin/pioneers/wall/${e.employer_id}/${action}`); toast.success(action === 'approve' ? 'Aprovado — já aparece no Mural de Confiança' : 'Retirado do Mural'); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  const upload = async (f) => {
    if (!f) return;
    const fd = new FormData(); fd.append('logo', f);
    setBusy(true);
    try { await api.post(`/admin/pioneers/wall/${e.employer_id}/logo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } }); toast.success('Logo trocado'); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  const requestLogo = async () => {
    if (!window.confirm(`Enviar a ${e.company_name} um pedido para carregar o logótipo?`)) return;
    setBusy(true);
    try { const { data } = await api.post(`/admin/pioneers/wall/${e.employer_id}/request-logo`); toast.success(`Pedido enviado para ${data.to}`); onChanged(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro'); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid={`mural-pioneer-${e.employer_id}`}>
      <AdminLogo url={e.logo_url} />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900">{e.company_name}</p>
        <p className="text-xs text-slate-600" data-testid={`mural-pioneer-state-${e.employer_id}`}>{MURAL_LABEL[e.mural_state]}{e.logo_source ? ` · logo ${SOURCE[e.logo_source] || e.logo_source}` : ''}</p>
        <p className="text-[11px] text-slate-400">{e.consent ? 'Autorização da empresa ✓' : 'Sem autorização — não pode ser publicado'}{e.suggested_from ? ` · ${e.suggested_from}` : ''}</p>
        {e.logo_request_sent_at && <p className="text-[11px] text-blue-700" data-testid={`mural-pioneer-requested-${e.employer_id}`}>Pedido de logo enviado a {new Date(e.logo_request_sent_at).toLocaleDateString('pt-PT')}{e.logo_request_count > 1 ? ` (${e.logo_request_count}×)` : ''}</p>}
      </div>
      <div className="flex flex-wrap gap-2">
        {e.consent && e.mural_state === 'awaiting_logo' && <Button size="sm" variant="outline" disabled={busy} className="border-blue-300 text-blue-800" onClick={requestLogo} data-testid={`mural-pioneer-request-logo-${e.employer_id}`}>Pedir logo à empresa</Button>}
        <input ref={file} type="file" accept="image/png,image/svg+xml,image/jpeg,image/webp" className="hidden" onChange={(ev) => { upload(ev.target.files?.[0]); ev.target.value = ''; }} data-testid={`mural-pioneer-file-${e.employer_id}`} />
        <Button size="sm" variant="outline" disabled={busy} onClick={() => file.current?.click()} data-testid={`mural-pioneer-replace-${e.employer_id}`}><Upload className="h-3.5 w-3.5 mr-1" />{e.logo_url ? 'Trocar logo' : 'Carregar logo'}</Button>
        {e.status !== 'approved' && <Button size="sm" disabled={busy || !e.consent || !e.logo_url} className="bg-emerald-600 hover:bg-emerald-700" onClick={() => act('approve')} data-testid={`mural-pioneer-approve-${e.employer_id}`}><CheckCircle className="h-3.5 w-3.5 mr-1" />Aprovar</Button>}
        {e.status !== 'rejected' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => act('reject')} data-testid={`mural-pioneer-reject-${e.employer_id}`}><XCircle className="h-3.5 w-3.5 mr-1" />{e.status === 'approved' ? 'Retirar' : 'Rejeitar'}</Button>}
      </div>
    </div>
  );
};

export const PioneerMuralPanel = ({ onChanged }) => {
  const [items, setItems] = useState(null);
  const load = () => api.get('/admin/pioneers/wall').then((r) => setItems(r.data.items)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  if (!items || items.length === 0) return null;
  const pending = items.filter((e) => ['awaiting_logo', 'awaiting_approval'].includes(e.mural_state));
  const others = items.filter((e) => !pending.includes(e));
  const changed = () => { load(); onChanged?.(); };
  return (
    <div className="mb-8 space-y-3" data-testid="mural-pioneers-panel">
      <h2 className="text-lg font-bold text-slate-900">Pioneiros no Mural <span className="ml-1 rounded-full bg-amber-100 px-2 text-sm text-amber-800" data-testid="mural-pioneers-pending-count">{pending.length} à espera</span></h2>
      <p className="text-xs text-slate-500">Só aparece no site depois de aprovar — e só com a autorização da empresa. Pode trocar o logo antes de aprovar.</p>
      {pending.map((e) => <Entry key={e.employer_id} e={e} onChanged={changed} />)}
      {others.length > 0 && <details className="rounded-lg border border-slate-200 bg-slate-50 p-3"><summary className="cursor-pointer text-sm font-semibold text-slate-700" data-testid="mural-pioneers-others">Publicados e rejeitados ({others.length})</summary><div className="mt-3 space-y-2">{others.map((e) => <Entry key={e.employer_id} e={e} onChanged={changed} />)}</div></details>}
    </div>
  );
};
